import { forwardRef, type ComponentRef } from "react";

import { cn } from "../../lib";
import {
  PopoverContentPrimitive,
  PopoverRootPrimitive,
  PopoverTriggerPrimitive,
} from "../../primitives";
import type { PopoverContentProps } from "./types";

/**
 * Popover root. Manages open/close state and provides context to the trigger
 * and content. Thin passthrough over the platform-split primitive.
 */
export const Popover = PopoverRootPrimitive;

/**
 * Popover trigger. Wrap the element that toggles the popover; use `asChild` to
 * merge props onto a custom child instead of rendering a default button.
 */
export const PopoverTrigger = PopoverTriggerPrimitive;

/**
 * Popover content — the floating surface anchored to the trigger.
 *
 * Carries the design-system card styling (rounded surface, shadow, tokens).
 * Positioning props (`side`, `align`, `sideOffset`, `alignOffset`) are handled
 * by the underlying primitive.
 */
export const PopoverContent = forwardRef<
  ComponentRef<typeof PopoverContentPrimitive>,
  PopoverContentProps
>(({ className, ...props }, ref) => (
  <PopoverContentPrimitive
    ref={ref}
    className={cn(
      "bg-luce-neutral-background-default text-luce-neutral-foreground-default shadow-modal rounded-radius-xl p-spacing-lg z-50 w-auto outline-none",
      // Radix's data-state enter/exit animation is web-only. On native NativeWind
      // left the surface at opacity 0, so the popover never showed
      "web:data-[state=open]:animate-in web:data-[state=closed]:animate-out web:data-[state=closed]:fade-out-0 web:data-[state=open]:fade-in-0 web:data-[state=closed]:zoom-out-95 web:data-[state=open]:zoom-in-95",
      className,
    )}
    {...props}
  />
));

PopoverContent.displayName = "PopoverContent";
